import type { ClientClassificationInput } from "@repo/shared/types";
import { useAnsweringSubjects } from "@/lib/hooks/use-flow-instances/use-answering-subjects";
import {
  useClientClassificationFlowInstanceValue,
  useSaveClientClassification,
} from "@/lib/hooks/use-flow-instances/use-client-classification";
import {
  useApplyPreviousAnswer,
  useReuseAvailability,
} from "@/lib/hooks/use-flow-instances/use-reuse-previous-answer";
import { ClientClassificationWidget } from "../client-classification-widget";
import type { ActingRepresentative } from "../representative-note";

type WizardClientClassificationProps = {
  instanceId: string;
  clientType: "Person" | "Company";
  clientName: string;
  actingRepresentative?: ActingRepresentative | null;
  isGroupClient?: boolean;
  disabled: boolean;
  style?: "none" | "border" | "box";
  title?: string;
  description?: string;
  hasValidationError?: boolean;
};

export function WizardClientClassification({
  instanceId,
  clientType,
  clientName,
  actingRepresentative,
  isGroupClient,
  disabled,
  style,
  title,
  description,
  hasValidationError,
}: WizardClientClassificationProps) {
  const { inputData, isLoading: valueLoading } =
    useClientClassificationFlowInstanceValue(instanceId);
  const {
    available,
    sourceDate,
    isLoading: availabilityLoading,
  } = useReuseAvailability(instanceId, "client_classification");
  // QL-660 — a group whose representative member acts through their own POA: the
  // server-decided attribution says whose POA, the same as the quiz and the
  // product assessment notes do. Everything else keeps the wizard's rule.
  const { attribution, isLoading: attributionLoading } = useAnsweringSubjects(instanceId);
  const viaMember =
    attribution?.kind === "representative" && attribution.memberRepresentative
      ? {
          name: attribution.name,
          count: 1,
          memberRepresentative: attribution.memberRepresentative,
        }
      : null;
  const saveClassification = useSaveClientClassification(instanceId);
  const applyPrevious = useApplyPreviousAnswer(instanceId, "client_classification");

  const handleChange = (input: ClientClassificationInput) => {
    saveClassification.mutate(input);
  };

  // Wait for the attribution too, so the note never flashes its member-naming text first.
  if (valueLoading || availabilityLoading || attributionLoading) return null;

  return (
    <ClientClassificationWidget
      clientType={clientType}
      actingRepresentative={viaMember ?? actingRepresentative}
      isGroupClient={isGroupClient}
      clientName={clientName}
      inputData={inputData}
      style={style}
      title={title}
      description={description}
      onChange={handleChange}
      disabled={disabled}
      available={available}
      sourceDate={sourceDate}
      onApply={async () => {
        await applyPrevious.mutateAsync();
      }}
      hasValidationError={hasValidationError}
    />
  );
}
